import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TrailCard from './components/TrailCard';
import ActivityTimeline from './components/ActivityTimeline';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import { getActivityHistory, getPerformanceInsights, getPerformanceAnalytics } from '../../services/studentProgressService';
import { getStudentRewards } from '../../services/rewardService';
import { getUnlockedTrails } from '../../services/studentUnlockService';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [studentSession, setStudentSession] = useState(null);
  const [trails, setTrails] = useState([]);
  const [activities, setActivities] = useState([]);
  const [insights, setInsights] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starBalance, setStarBalance] = useState({ totalStars: 0, starsAvailable: 0 });

  const fetchDashboardData = useCallback(async (session) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch only the student's personally unlocked trails
      const { trails: unlockedTrails, error: trailsError } = await getUnlockedTrails(session?.id);
      if (trailsError) {
        console.error('Failed to fetch unlocked trails:', trailsError);
        setError(trailsError);
      } else {
        setTrails(unlockedTrails || []);
      }

      // Fetch activity history
      const { activities: activityData, error: activityError } = await getActivityHistory(session?.id);
      if (activityError) {
        console.error('Failed to fetch activities:', activityError);
      } else {
        setActivities(activityData || []);
      }

      // Fetch performance insights
      const { insights: insightsData, error: insightsError } = await getPerformanceInsights(session?.id);
      if (insightsError) {
        console.error('Failed to fetch insights:', insightsError);
      } else {
        const notStartedCount = (unlockedTrails || [])?.filter(t => !t?.studentProgress)?.length || 0;
        setInsights({
          ...insightsData,
          notStartedTrails: notStartedCount
        });
      }

      // Fetch performance analytics
      const { analytics: analyticsData } = await getPerformanceAnalytics(session?.id);
      const notStartedCount = (unlockedTrails || [])?.filter(t => !t?.studentProgress)?.length || 0;
      setAnalytics({
        ...analyticsData,
        notStartedTrails: notStartedCount
      });

      // Fetch star balance
      const { rewards } = await getStudentRewards(session?.id);
      if (rewards) setStarBalance(rewards);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const session = localStorage.getItem('studentSession');
    if (!session) {
      navigate('/student-login');
      return;
    }

    try {
      const parsedSession = JSON.parse(session);
      setStudentSession(parsedSession);
      fetchDashboardData(parsedSession);
    } catch (error) {
      console.error('Error parsing session:', error);
      navigate('/student-login');
    }
  }, [navigate, fetchDashboardData]);

  // Refresh data when the page becomes visible again (e.g. returning from trail view)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const session = localStorage.getItem('studentSession');
        if (session) {
          try {
            const parsedSession = JSON.parse(session);
            fetchDashboardData(parsedSession);
          } catch (e) {
            // ignore
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchDashboardData]);

  const handleStartTrail = async (trailId) => {
    navigate(`/student-learning-trail-view?trailId=${trailId}`);
  };

  const handleSignOut = () => {
    localStorage.removeItem('studentSession');
    navigate('/student-login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                <Icon name="BookOpen" size={24} className="text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                LearningTrails
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Star Balance in Header */}
              <button
                onClick={() => navigate('/student-rewards-center')}
                className="flex items-center space-x-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-full transition-colors"
              >
                <span className="text-base">⭐</span>
                <span className="text-sm font-bold text-amber-700">{starBalance?.starsAvailable || 0}</span>
                <span className="text-xs text-amber-600 hidden sm:inline">stars</span>
              </button>
              <div className="hidden md:flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Icon name="User" size={16} className="text-blue-500" />
                <span className="text-sm font-medium text-foreground">{studentSession?.fullName}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                iconName="LogOut"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">Unable to Load Dashboard</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Welcome Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                  Welcome back, {studentSession?.fullName}! 👋
                </h1>
                <p className="text-sm text-muted-foreground">
                  Scan QR codes to unlock new learning adventures
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() => navigate('/student-rewards-center')}
                  variant="outline"
                  iconName="Star"
                  className="border-amber-300 text-amber-600 hover:bg-amber-50"
                >
                  Rewards
                </Button>
                <Button
                  onClick={() => navigate('/qr-code-scanner')}
                  iconName="QrCode"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg shadow-blue-200 px-6"
                >
                  Scan QR Code
                </Button>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Trails */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground flex items-center">
                    <Icon name="Map" size={24} className="mr-2 text-blue-500" />
                    My Unlocked Trails
                    {trails?.length > 0 && (
                      <span className="ml-2 text-sm font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {trails?.length}
                      </span>
                    )}
                  </h2>
                </div>

                {trails?.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <Icon name="QrCode" size={36} className="text-blue-500" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">No trails yet!</h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Tap Scan QR Code to start your first adventure!
                    </p>
                    <Button
                      onClick={() => navigate('/qr-code-scanner')}
                      iconName="QrCode"
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg shadow-blue-200"
                    >
                      Scan QR Code
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {trails?.map((trail) => (
                      <TrailCard
                        key={trail?.id}
                        trail={trail}
                        onStart={handleStartTrail}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Timeline */}
              <ActivityTimeline activities={activities} />
            </div>

            {/* Right Column - Analytics */}
            <div className="space-y-6">
              <PerformanceAnalytics analytics={analytics} />
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-36 h-36 bg-pink-200 rounded-full opacity-20 blur-3xl"></div>
      </div>
    </div>
  );
};

export default StudentDashboard;